import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAlert } from '../contexts/AlertContext';
import { useAuth } from '../contexts/AuthContext';

export const useTokenValidation = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { validateToken } = useAuth();
    const [isValidating, setIsValidating] = useState(false);

    // Problem: To remove showAlert from the dependency array from the useEffect
    // Fix: Use useRef to store the latest showAlert function
    const showAlertRef = useRef(showAlert);
    // Problem: The most likely cause is React Strict Mode in development, which intentionally mounts components twice
    // to help detect side effects. This would cause the useEffect to run twice with the same token present.
    // Fix: Add a flag to prevent duplicate validation
    const isValidatingRef = useRef(false);

    useEffect(() => {
        showAlertRef.current = showAlert;
    }, [showAlert]);

    useEffect(() => {
        const token = searchParams.get('token');

        if (token && !isValidatingRef.current) {
            isValidatingRef.current = true;
            setIsValidating(true);

            const handleTokenValidation = async () => {
                try {
                    showAlertRef.current('Validating access token...', 'info', 1000);
                    const result = await validateToken(decodeURIComponent(token));

                    // Clear token from URL
                    setSearchParams(prev => {
                        prev.delete('token');
                        return prev;
                    });

                    // Check if user is a company primary contact for their specific customer code
                    const customerCode = result.customerData?.customerCode;
                    const matchedCustomer = result.customerData?.customerList?.find(
                        (customer: any) => customer.customer_code === customerCode
                    );
                    const isPrimaryContact = matchedCustomer?.company_primary_contact === true;

                    if (isPrimaryContact) {
                        showAlertRef.current(
                            '<center>Access granted!</center><br>Redirecting you to company page ...',
                            'success',
                            1000,
                        () => navigate('/company', { state: { from: 'tokenValidation' } }));
                    } else {
                        showAlertRef.current(
                            '<center>Access granted!</center><br>Redirecting you to your profile page ...',
                            'success',
                            1000,
                        () => navigate('/profile', { state: { from: 'tokenValidation' } }));
                    }
                } catch (error) {
                    showAlertRef.current('Invalid or expired access token. Please login manually.', 'error');
                    setSearchParams(prev => {
                        prev.delete('token');
                        return prev;
                    });
                    setIsValidating(false);
                }
            };

            handleTokenValidation();
        }
    }, [searchParams, navigate, setSearchParams, validateToken]);

    return { isValidating };
};